import {
    Box,
    Button,
    Chip,
    Paper,
    Stack,
    Tab,
    Tabs
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { updateAllTrailersInfo } from '../../api/trailerApi';
import { URL_PREFIX } from '../../constants/stores';
import { useAppDialog } from '../../Hooks/useAppDialog';
import { ZONE_OPTIONS } from '../../Utils/utils';
import AppDialogMessage from '../Common/AppDialogMessage';
import { Trailer } from './trailer';
import TrailerUnit from './TrailerUnit';
import { useTrailerStore } from './useTrailerStore';

type YardFilter = {
    trailer_no: string;
    product: string;
    zone: string;
};

export default function YardMap() {
    const { trailers, addTrailer, emptyTrailer } = useTrailerStore();
    const token = useSelector((state: any) => state.user.sessionId);

    const initialFilter = useMemo<YardFilter>(
        () => ({ trailer_no: '', product: '', zone: 'WEST' }),
        []
    );

    const [filter, setFilter] = useState<YardFilter>(initialFilter);
    const [activeZone, setActiveZone] = useState<string>('WEST');
    const [map, setMap] = useState<string>('/WEST_SIDE_MAP.png');
    const [modifiedTrailers, setModifiedTrailers] = useState<Set<string>>(new Set());
    const { dialog, openDialog, closeDialog } = useAppDialog();

    const markTrailerAsModified = useCallback((id: string) => {
        setModifiedTrailers((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);

    const saveChanges = useCallback(async () => {
        try {
            const modifiedIds = Array.from(modifiedTrailers);

            const trailersToSave = trailers.filter((trailer: any) =>
                modifiedIds.includes(trailer.id)
            );

            if (trailersToSave.length === 0) {
                openDialog({
                    variant: 'info',
                    title: 'No Changes',
                    message: 'No changes to save.',
                    confirmText: 'Okay',
                    hideCancel: true,
                });
                return;
            }

            const result = await updateAllTrailersInfo(trailersToSave);

            setModifiedTrailers(new Set());

            openDialog({
                variant: 'success',
                title: 'Updated',
                message: result.message || 'Trailer info updated successfully',
                confirmText: 'Okay',
                hideCancel: true,
            });
        } catch (error: any) {
            console.log('Error saving trailer changes:', error);

            openDialog({
                variant: 'error',
                title: 'Update Failed',
                message: error?.message || 'Error in updating trailer info',
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    }, [modifiedTrailers, trailers]);

    const getTrailers = useCallback(
        async (filterPayload?: Partial<YardFilter>) => {
            try {
                const response = await fetch(`${URL_PREFIX}/trailer/getAllTrailers`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        ...(filterPayload || {}),
                    }),
                });

                const data = await response.json();

                if (response.status === 200) {
                    emptyTrailer();

                    (data?.data || []).forEach((element: any) => {
                        addTrailer({
                            id: element.id,
                            x: element?.info?.x || 0,
                            y: element?.info?.y || 0,
                            width: element?.info?.width || 50,
                            height: element?.info?.height || 100,
                            orientation: element?.info?.orientation || 'horizontal',
                            lat: element?.info?.lat || '',
                            lon: element?.info?.lon || '',
                            zone: element?.info?.zone || '',
                            details: {
                                name: element?.info?.trailer_no || '',
                                status: element?.info?.status || '',
                            },
                        });
                    });

                    setModifiedTrailers(new Set());
                }
            } catch (error) {
                console.log('Error in getAllTrailers:', error);
            }
        },
        [addTrailer, emptyTrailer, token]
    );

    useEffect(() => {
        getTrailers(filter);
    }, [filter, getTrailers]);

    const handleZoneChange = useCallback((_e: React.SyntheticEvent, value: string) => {
        const selected = ZONE_OPTIONS.find((z: any) => z.label === value);
        if (!selected) return;

        setActiveZone(selected.label);
        setFilter((prev) => ({ ...prev, zone: selected.label }));
        setMap(`/MAP_${selected.value}_SIDE.png`);
    }, []);

    return (
        <>
            <Stack spacing={2}>
                <Paper sx={{ px: 2, pt: 0 }}>
                    <Tabs
                        value={activeZone}
                        onChange={handleZoneChange}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {ZONE_OPTIONS.map((zone: any) => (
                            <Tab
                                key={zone.value}
                                value={zone.label}
                                label={zone.label}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            />
                        ))}
                    </Tabs>
                </Paper>

                <Paper
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: '71svh',
                        overflow: 'auto',
                        border: '2px dashed',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: '4000px',
                            height: '1400px',
                            backgroundImage: `url(${map})`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'contain',
                            backgroundPosition: 'top left',
                        }}
                    >
                        {trailers.map((trailer: Trailer) => (
                            <TrailerUnit
                                key={trailer.id}
                                trailer={trailer}
                                onTrailerUpdate={markTrailerAsModified}
                            />
                        ))}
                    </Box>
                </Paper>

                <Paper
                    sx={{
                        p: 1.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    <Chip
                        color={modifiedTrailers.size > 0 ? 'warning' : 'default'}
                        label={`Unsaved changes: ${modifiedTrailers.size}`}
                    />

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            onClick={saveChanges}
                            disabled={modifiedTrailers.size === 0}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </Paper>
            </Stack>
            <AppDialogMessage
                open={dialog.open}
                title={dialog.title}
                message={dialog.message}
                variant={dialog.variant}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                hideCancel={dialog.hideCancel}
                onConfirm={dialog.onConfirm}
                onClose={closeDialog}
            />
        </>
    );
}